import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { notificationsApi } from '../../lib/api';
import { usePostgresChanges } from '../../hooks/useSupabaseRealtime';
import { useAuth } from '../../contexts/AuthContext';
import { Bell, Check, CheckCheck, X, ChevronRight } from 'lucide-react';

const TYPE_ICONS = {
  poll_new: 'vote', poll_closing: 'vote', draft_live: 'trophy', team_on_clock: 'zap',
  team_draft_pick: 'trophy', team_update: 'shield', announcement: 'megaphone',
  game_reminder: 'calendar', game_starting: 'zap', highlight_posted: 'play',
  discussion_activity: 'message', engagement_milestone: 'star',
  roster_assigned: 'users', roster_updated: 'users', travel_itinerary: 'plane',
  profile_approved: 'check', profile_needs_changes: 'alert', added_to_draft: 'trophy',
  drafted_by_team: 'trophy', ops_alert: 'alert', admin_manual: 'bell',
};

const PRIORITY_COLORS = {
  urgent: 'border-l-red-500',
  high: 'border-l-orange-400',
  normal: 'border-l-transparent',
  low: 'border-l-transparent',
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export const NotificationBell = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const { user } = useAuth();

  const fetchCount = useCallback(async () => {
    try {
      const res = await notificationsApi.getUnreadCount();
      setUnreadCount(res.data.count);
    } catch {}
  }, []);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  // Supabase Realtime: Instant notification on INSERT
  usePostgresChanges('notifications', 'INSERT', useCallback((payload) => {
    const newNotif = payload.new;
    if (newNotif.user_id !== user?.id) return;
    setUnreadCount(prev => prev + 1);
    // Prepend to open dropdown if visible
    setNotifications(prev => {
      if (prev.length === 0) return prev;
      return [newNotif, ...prev].slice(0, 8);
    });
  }, [user?.id]), user?.id ? { column: 'user_id', value: user.id } : undefined);

  useEffect(() => {
    if (open) {
      setLoading(true);
      notificationsApi.getAll({ limit: 8 }).then(res => {
        setNotifications(res.data);
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [open]);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleMarkRead = async (id, e) => {
    e.stopPropagation();
    e.preventDefault();
    await notificationsApi.markRead(id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, status: 'read' } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const handleMarkAllRead = async () => {
    await notificationsApi.markAllRead();
    setNotifications(prev => prev.map(n => ({ ...n, status: 'read' })));
    setUnreadCount(0);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-3 lg:p-2 min-h-[44px] min-w-[44px] lg:min-h-0 lg:min-w-0 inline-flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full" data-testid="notif-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-card border border-border shadow-xl z-50" data-testid="notif-dropdown">
          <div className="p-3 border-b border-border flex items-center justify-between">
            <h3 className="font-heading font-bold uppercase text-xs tracking-wider">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button onClick={handleMarkAllRead} className="text-[10px] text-primary font-mono hover:text-primary/80" data-testid="mark-all-read-btn">
                  <CheckCheck className="w-3.5 h-3.5 inline mr-1" />Mark all read
                </button>
              )}
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-8"><div className="animate-spin w-5 h-5 border-2 border-primary border-t-transparent rounded-full" /></div>
            ) : notifications.length > 0 ? (
              notifications.map(n => (
                <div
                  key={n.id}
                  className={`border-l-2 ${PRIORITY_COLORS[n.priority] || ''} ${n.status === 'unread' ? 'bg-primary/5' : ''} hover:bg-muted/10 transition-colors`}
                >
                  {n.action_url ? (
                    <Link to={n.action_url} onClick={() => { if (n.status === 'unread') handleMarkRead(n.id, { stopPropagation: () => {}, preventDefault: () => {} }); setOpen(false); }} className="block px-3 py-2.5">
                      <NotifItem n={n} onMarkRead={handleMarkRead} />
                    </Link>
                  ) : (
                    <div className="px-3 py-2.5">
                      <NotifItem n={n} onMarkRead={handleMarkRead} />
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="py-8 text-center text-sm text-muted-foreground">No notifications yet</div>
            )}
          </div>

          <Link
            to="/notifications"
            onClick={() => setOpen(false)}
            className="block p-3 text-center border-t border-border text-xs text-primary font-heading uppercase tracking-wider hover:bg-muted/10 transition-colors"
            data-testid="view-all-notifs"
          >
            View All Notifications <ChevronRight className="w-3 h-3 inline" />
          </Link>
        </div>
      )}
    </div>
  );
};

const NotifItem = ({ n, onMarkRead }) => (
  <div className="flex items-start gap-2">
    <div className="flex-1 min-w-0">
      <p className={`text-xs font-heading uppercase leading-tight ${n.status === 'unread' ? 'font-bold' : 'text-muted-foreground'}`}>
        {n.title}
      </p>
      <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-1">{n.message}</p>
      <p className="text-[9px] text-muted-foreground font-mono mt-1">{timeAgo(n.created_at)}</p>
    </div>
    {n.status === 'unread' && (
      <button onClick={(e) => onMarkRead(n.id, e)} className="p-1 text-muted-foreground hover:text-primary flex-shrink-0 mt-0.5" title="Mark as read">
        <Check className="w-3 h-3" />
      </button>
    )}
  </div>
);
